// Automatizaciones · detalle de un flujo: métricas, historial (filtros + CSV), configuración y prueba manual.
import type { JSX } from "react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Download, FlaskConical, Pencil, RefreshCw } from "lucide-react";
import { api, ApiError } from "../../../lib/api.ts";
import { useMe } from "../../../lib/auth.ts";
import { formatDateTime } from "../../../lib/format.ts";
import { Badge, Button, Card, EmptyState, ErrorState, Field, Input, Loading, Select, TableSkeleton } from "../../../components/ui.tsx";
import { toast } from "../../../components/toast.tsx";
import { type Flow, LOG_STATUS, RUN_STATUS, STEP_META, Stat, Toggle, TRIGGER_LABELS } from "./shared.tsx";

interface HistoryRow {
  id: string; createdAt: string; contactId: string | null; contactName: string; email: string | null; step: number | null;
  action: string; actionLabel: string; status: string; scheduledFor: string | null; observations: string | null; runStatus: string;
}
interface Filters { from: string; to: string; contact: string; step: string; status: string }
const EMPTY: Filters = { from: "", to: "", contact: "", step: "", status: "" };

export function FlowDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const admin = Boolean(me.data?.admin);
  const [tab, setTab] = useState<"history" | "config">("history");
  const q = useQuery<{ flow: Flow; runs: Record<string, number> }>({ queryKey: ["mkt-flow", id], queryFn: () => api.get(`/mkt/flows/${id}`) });

  const state = useMutation({
    mutationFn: (b: { isActive?: boolean; status?: "draft" | "published" }) => api.patch(`/mkt/flows/${id}/state`, b),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["mkt-flow", id] }); void qc.invalidateQueries({ queryKey: ["mkt-flows"] }); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo actualizar."),
  });

  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <ErrorState message={q.error instanceof ApiError ? q.error.message : "No se pudo cargar el flujo."} onRetry={() => void q.refetch()} />;
  const f = q.data.flow;
  const published = f.status === "published";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button className="btn-ghost btn-sm" onClick={() => nav("/marketing/automatizaciones")}><ArrowLeft size={16} strokeWidth={1.75} /> Automatizaciones</button>
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex items-center gap-2 text-sm text-ink-secondary">
            Activa <Toggle checked={f.isActive} label="Activa" disabled={!admin || state.isPending} onChange={(v) => state.mutate({ isActive: v })} />
          </span>
          {admin && (
            <Button onClick={() => state.mutate({ status: published ? "draft" : "published" })} disabled={state.isPending}>
              {published ? "Despublicar" : "Publicar"}
            </Button>
          )}
          {admin && <Button onClick={() => nav("editar")}><Pencil size={16} strokeWidth={1.75} /> Editar</Button>}
        </div>
      </div>

      <div>
        <h2 className="text-xl font-semibold text-ink flex items-center gap-2 flex-wrap">
          {f.name} {published ? <Badge color="green">Publicado</Badge> : <Badge>Borrador</Badge>}
        </h2>
        <p className="text-sm text-ink-muted mt-0.5">{f.triggerLabel} · Propietario: {f.ownerName ?? "—"} · Creado {formatDateTime(f.createdAt)}</p>
      </div>

      {f.source === "clientify" && f.steps.length === 0 && (
        <Card className="p-3 text-sm text-ink-secondary">
          Importado de Clientify: su API no entrega el lanzador ni los pasos. Edítalo para recrearlos antes de publicarlo.
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat label="Completado" value={f.completed} />
        <Stat label="Pendiente" value={f.pending} hint={q.data.runs["waiting"] ? `${q.data.runs["waiting"]} en espera` : undefined} />
        <Stat label="Objetivo cumplido" value={f.goalReached} hint={(q.data.runs["failed"] ?? 0) || (q.data.runs["stopped"] ?? 0) ? `${q.data.runs["stopped"] ?? 0} detenidos · ${q.data.runs["failed"] ?? 0} con error` : undefined} />
      </div>

      {admin && <TestPanel flowId={f.id} onDone={() => { void qc.invalidateQueries({ queryKey: ["mkt-flow", id] }); void qc.invalidateQueries({ queryKey: ["mkt-flow-history", id] }); }} />}

      <div className="flex gap-1 border-b border-line">
        {([["history", "Historial"], ["config", "Detalle y configuración"]] as const).map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === k ? "border-primary text-primary font-medium" : "border-transparent text-ink-secondary hover:text-ink"}`}>{label}</button>
        ))}
      </div>
      {tab === "history" ? <History flow={f} /> : <ConfigView flow={f} />}
    </div>
  );
}

// ────────── Historial ──────────
function History({ flow }: { flow: Flow }): JSX.Element {
  const [draft, setDraft] = useState<Filters>(EMPTY);
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const qs = (extra: Record<string, string>): string => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries({ ...filters, ...extra })) if (v) p.set(k, v);
    return p.toString();
  };
  const h = useQuery<{ total: number; rows: HistoryRow[] }>({
    queryKey: ["mkt-flow-history", flow.id, filters, page],
    queryFn: () => api.get(`/mkt/flows/${flow.id}/history?${qs({ page: String(page), pageSize: String(pageSize) })}`),
    refetchInterval: 30_000,
  });
  const pages = Math.max(1, Math.ceil((h.data?.total ?? 0) / pageSize));
  const apply = (): void => { setFilters(draft); setPage(1); };

  return (
    <Card className="p-0 overflow-hidden">
      <div className="p-3 border-b border-line grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
        <Field label="Desde"><Input type="date" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} /></Field>
        <Field label="Hasta"><Input type="date" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} /></Field>
        <Field label="Contacto"><Input value={draft.contact} placeholder="Nombre o email" onChange={(e) => setDraft({ ...draft, contact: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") apply(); }} /></Field>
        <Field label="Paso">
          <Select value={draft.step} onChange={(e) => setDraft({ ...draft, step: e.target.value })}>
            <option value="">Todos</option>
            <option value="enter">Entrada al flujo</option>
            {flow.steps.map((s, i) => <option key={s.id} value={String(i + 1)}>{i + 1}. {STEP_META[s.type].label}</option>)}
            <option value="goal">Objetivo cumplido</option>
            <option value="finish">Fin del flujo</option>
          </Select>
        </Field>
        <Field label="Estado">
          <Select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}>
            <option value="">Todos</option>
            {Object.entries(LOG_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </Select>
        </Field>
        <div className="flex gap-2">
          <Button onClick={apply}>Filtrar</Button>
          <a className="btn-secondary" href={`/api/v1/mkt/flows/${flow.id}/history.csv?${qs({})}`} title="Exportar CSV"><Download size={16} strokeWidth={1.75} /></a>
          <button className="btn-ghost btn-sm" title="Actualizar" onClick={() => void h.refetch()}><RefreshCw size={15} strokeWidth={1.75} /></button>
        </div>
      </div>
      {h.isLoading ? <TableSkeleton rows={6} cols={6} /> : h.isError ? <ErrorState onRetry={() => void h.refetch()} /> : !h.data?.rows.length ? (
        <EmptyState title="Sin movimientos" body="Aquí aparecerá cada acción ejecutada para cada contacto." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-ink-muted border-b border-line">
              <tr>
                <th className="px-3 py-2">Fecha</th><th className="px-3 py-2">Contacto</th><th className="px-3 py-2">Acción</th>
                <th className="px-3 py-2">Fecha estimada</th><th className="px-3 py-2">Estatus</th><th className="px-3 py-2">Flujo</th><th className="px-3 py-2">Observaciones</th>
              </tr>
            </thead>
            <tbody>
              {h.data.rows.map((r) => (
                <tr key={r.id} className="border-b border-line last:border-0 align-top">
                  <td className="px-3 py-2 whitespace-nowrap text-ink-secondary">{formatDateTime(r.createdAt)}</td>
                  <td className="px-3 py-2">
                    {r.contactId ? <Link className="text-primary hover:underline" to={`/contacts/${r.contactId}`}>{r.contactName}</Link> : r.contactName}
                    {r.email && <div className="text-xs text-ink-muted">{r.email}</div>}
                  </td>
                  <td className="px-3 py-2">{r.step ? `${r.step}. ` : ""}{r.actionLabel}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-ink-secondary">{r.scheduledFor ? formatDateTime(r.scheduledFor) : "—"}</td>
                  <td className="px-3 py-2"><Badge color={LOG_STATUS[r.status]?.color ?? "gray"}>{LOG_STATUS[r.status]?.label ?? r.status}</Badge></td>
                  <td className="px-3 py-2 text-ink-secondary whitespace-nowrap">{RUN_STATUS[r.runStatus] ?? r.runStatus}</td>
                  <td className="px-3 py-2 text-ink-secondary">{r.observations ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex items-center justify-between gap-2 p-3 border-t border-line text-sm text-ink-secondary">
        <span>{h.data?.total ?? 0} registros</span>
        <div className="flex items-center gap-2">
          <Button small disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
          <span>{page} / {pages}</span>
          <Button small disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
        </div>
      </div>
    </Card>
  );
}

// ────────── Configuración (lectura) ──────────
function ConfigView({ flow }: { flow: Flow }): JSX.Element {
  const s = flow.settings;
  const goal = s.goal.type === "none" ? "Sin objetivo" : s.goal.type === "deal_won" ? "Oportunidad ganada" : s.goal.type === "status" ? `Estado = ${s.goal.value}` : `Etiqueta ${s.goal.value}`;
  const yes = (b: boolean): string => (b ? "Sí" : "No");
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="p-5">
        <h3 className="text-sm font-semibold text-ink mb-1">Disparador</h3>
        <p className="text-sm text-ink-secondary mb-4">{TRIGGER_LABELS[flow.trigger.type] ? `${TRIGGER_LABELS[flow.trigger.type]} — ` : ""}{flow.triggerLabel}</p>
        <h3 className="text-sm font-semibold text-ink mb-2">Pasos</h3>
        {flow.steps.length === 0 ? <p className="text-sm text-ink-muted">Sin pasos.</p> : (
          <ol className="space-y-1.5">
            {flow.steps.map((st, i) => {
              const m = STEP_META[st.type]; const Icon = m.icon;
              return (
                <li key={st.id} className="flex items-center gap-2 text-sm text-ink-secondary">
                  <span className="w-5 text-right text-ink-muted">{i + 1}.</span><Icon size={14} strokeWidth={1.75} className="text-primary" />
                  {m.label}<span className="text-ink-muted truncate">{summary(st.config)}</span>
                </li>
              );
            })}
          </ol>
        )}
      </Card>
      <Card className="p-5 text-sm text-ink-secondary space-y-1.5">
        <h3 className="text-sm font-semibold text-ink mb-1">Configuración</h3>
        <div>Pasa solo una vez: <b className="text-ink">{yes(s.oncePerContact)}</b></div>
        <div>No puede estar en más de un flujo a la vez: <b className="text-ink">{yes(s.singleFlow)}</b></div>
        <div>Objetivo: <b className="text-ink">{goal}</b></div>
        <div>Detener al alcanzar el objetivo: <b className="text-ink">{yes(s.stopOnGoal)}</b></div>
        <div>Detener si agenda una reunión: <b className="text-ink">{yes(s.stopOnMeeting)}</b></div>
        <div>Múltiples oportunidades por contacto: <b className="text-ink">{yes(s.multipleDeals)}</b></div>
      </Card>
    </div>
  );
}

function summary(c: Record<string, unknown>): string {
  const v = c["subject"] ?? c["tag"] ?? c["name"] ?? c["status"] ?? (c["points"] !== undefined ? `${String(c["points"])} pts` : undefined)
    ?? (c["amount"] !== undefined && c["unit"] ? `${String(c["amount"])} ${String(c["unit"]) === "days" ? "días" : String(c["unit"]) === "hours" ? "horas" : "min"}` : undefined);
  return v ? `· ${String(v)}` : "";
}

// ────────── Prueba manual ──────────
function TestPanel({ flowId, onDone }: { flowId: string; onDone: () => void }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const found = useQuery<{ contacts: Array<{ id: string; firstName: string | null; lastName: string; email: string | null }> }>({
    queryKey: ["mkt-flow-contacts", term], queryFn: () => api.get(`/mkt/flows/contacts?q=${encodeURIComponent(term)}`), enabled: open && term.trim().length >= 2,
  });
  const test = useMutation({
    mutationFn: (contactId: string) => api.post(`/mkt/flows/${flowId}/test`, { contactId }),
    onSuccess: () => { toast.success("Contacto inscrito en el flujo (prueba). Revisa el historial."); setOpen(false); setTerm(""); setTimeout(onDone, 1500); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo iniciar la prueba."),
  });
  if (!open) return <div><Button small onClick={() => setOpen(true)}><FlaskConical size={15} strokeWidth={1.75} /> Probar con un contacto</Button></div>;
  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-ink">Probar flujo con un contacto</h3>
        <button className="btn-ghost btn-sm" onClick={() => setOpen(false)} aria-label="Cerrar">✕</button>
      </div>
      <p className="text-xs text-ink-muted">Ejecuta los pasos reales (emails, oportunidades, tareas…) para el contacto elegido, aunque el flujo esté en borrador.</p>
      <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Buscar contacto por nombre o email…" />
      <div className="divide-y divide-line">
        {(found.data?.contacts ?? []).map((c) => (
          <div key={c.id} className="flex items-center justify-between gap-2 py-1.5 text-sm">
            <span className="text-ink">{`${c.firstName ?? ""} ${c.lastName}`.trim()} <span className="text-ink-muted">{c.email ?? ""}</span></span>
            <Button small onClick={() => test.mutate(c.id)} disabled={test.isPending}>Probar</Button>
          </div>
        ))}
      </div>
    </Card>
  );
}
